import { type ErrorLike } from '@apollo/client';
import { useApolloClient, useSuspenseQuery } from '@apollo/client/react';
import { useParams } from 'react-router';
import { useState } from 'react';

import { ContinuityProviderStatusEnum } from 'src/graphql/schema';
import {
  ContinuityEscrowDocument,
  type IContinuityEscrowQuery,
} from 'src/graphql/actions/continuityEscrow/continuityEscrow.query';

import { createRequiredContext } from '../createRequiredContext';

function assertDataExists(
  error: ErrorLike | undefined,
  data: IContinuityEscrowQuery | undefined,
): asserts data is IContinuityEscrowQuery & {
  continuityEscrow: NonNullable<IContinuityEscrowQuery['continuityEscrow']>;
} {
  if (error) throw error;
  if (!data?.continuityEscrow) throw new Error('Continuity escrow data not found');
}

type IUiState = {
  isTerminationDialogOpen: boolean;
};

const [useContinuityEscrow, ContinuityEscrowProvider] = createRequiredContext<{
  continuityEscrow: NonNullable<IContinuityEscrowQuery['continuityEscrow']>;
  refetch: () => void;
  hasUnconfirmedProvider: boolean;
  hasAccessIssuesProvider: boolean;
  isEveryProviderConfirmed: boolean;
  ui: IUiState;
  setIsTerminationDialogOpen: (isOpen: boolean) => void;
}>();

const ContinuityEscrowContextProvider = ({ children }: React.PropsWithChildren) => {
  const [isTerminationDialogOpen, setIsTerminationDialogOpen] = useState(false);
  const { id: continuityEscrowId = '' } = useParams<{ id: string }>();
  const client = useApolloClient();
  const { data, error } = useSuspenseQuery(ContinuityEscrowDocument, {
    variables: { continuityEscrowId },
    skip: !continuityEscrowId,
  });

  assertDataExists(error, data);

  const refetch = () => client.refetchQueries({ include: [ContinuityEscrowDocument] });

  const allProviders = [...data.continuityEscrow.hostingProviders, ...data.continuityEscrow.thirdPartyProviders];

  const hasUnconfirmedProvider = allProviders.some(({ status }) => status === ContinuityProviderStatusEnum.Unconfirmed);

  const hasAccessIssuesProvider = allProviders.some(
    ({ status }) => status === ContinuityProviderStatusEnum.AccessIssues,
  );

  const isEveryProviderConfirmed = allProviders.every(
    ({ status }) => status === ContinuityProviderStatusEnum.Confirmed,
  );

  const providerValue = {
    continuityEscrow: data.continuityEscrow,
    refetch,
    hasUnconfirmedProvider,
    hasAccessIssuesProvider,
    isEveryProviderConfirmed,
    ui: { isTerminationDialogOpen },
    setIsTerminationDialogOpen,
  };

  return <ContinuityEscrowProvider value={providerValue}>{children}</ContinuityEscrowProvider>;
};

export { useContinuityEscrow, ContinuityEscrowContextProvider };
